import React from 'react';
import { Card, CardContent } from './ui/card';
import { Truck, Package, Ruler, Weight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import Tilt from 'react-parallax-tilt';

export const Fleet = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const fleetVehicles = [
    {
      name: "Small Van",
      image: "https://customer-assets.emergentagent.com/job_express-booking-1/artifacts/5t5he0kl_download.jpg",
      specs: {
        length: "1.50m",
        height: "1.10m",
        width: "1.20m",
        pallets: "Up to 1 pallet",
        maxWeight: "350kg"
      },
      description: "Perfect for small parcels and documents",
      color: "from-blue-500 to-blue-600"
    },
    {
      name: "Short Wheel Base",
      image: "https://customer-assets.emergentagent.com/job_express-booking-1/artifacts/oah4sap4_swb.jpg",
      specs: {
        length: "2.40m",
        height: "1.40m",
        width: "1.60m",
        pallets: "Up to 2 pallets",
        maxWeight: "650kg"
      },
      description: "Ideal for multiple packages and small pallets",
      color: "from-emerald-500 to-emerald-600"
    },
    {
      name: "Medium Wheel Base",
      image: "https://customer-assets.emergentagent.com/job_express-booking-1/artifacts/oi5hc7w2_mwb%20van.jpg",
      specs: {
        length: "2.90m",
        height: "1.70m",
        width: "1.70m",
        pallets: "Up to 3 pallets",
        maxWeight: "900kg"
      },
      description: "Great for standard commercial deliveries",
      color: "from-purple-500 to-purple-600"
    },
    {
      name: "Extra Long Wheel Base",
      image: "https://customer-assets.emergentagent.com/job_express-booking-1/artifacts/615qlu0n_xlw.jpg",
      specs: {
        length: "4.20m",
        height: "2.00m",
        width: "1.75m",
        pallets: "Up to 4 pallets",
        maxWeight: "1250kg"
      },
      description: "Maximum van capacity for bulk deliveries",
      color: "from-red-500 to-red-600"
    },
    {
      name: "Luton Van",
      image: "https://customer-assets.emergentagent.com/job_express-booking-1/artifacts/kw5o7u4l_luton.jpg",
      specs: {
        length: "4.00m",
        height: "2.00m",
        width: "2.00m",
        pallets: "Up to 4 pallets",
        maxWeight: "1200kg"
      },
      description: "Box van with tail lift for heavy items",
      color: "from-indigo-500 to-indigo-600"
    },
    {
      name: "7.5 Tonne Truck",
      image: "https://customer-assets.emergentagent.com/job_express-booking-1/artifacts/rtn6ca1k_7.5-tonne-truck_3.jpg",
      specs: {
        length: "6.00m",
        height: "2.00m",
        width: "2.30m",
        pallets: "Up to 10 pallets",
        maxWeight: "2700kg"
      },
      description: "Large capacity for major commercial shipments",
      color: "from-cyan-500 to-cyan-600"
    }
  ];

  return (
    <section id="fleet" className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 right-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-float" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-float-delayed" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl mb-4">
            <Truck className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Our Fleet
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Professional vehicles for every delivery need. From small parcels to bulk shipments, we have the right vehicle for your business.
          </p>
        </div>

        {/* Fleet Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {fleetVehicles.map((vehicle, index) => (
            <Tilt
              key={index}
              className={`transform transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              tiltMaxAngleX={8}
              tiltMaxAngleY={8}
              perspective={1000}
              scale={1.02}
              transitionSpeed={2000}
              glareEnable={true}
              glareMaxOpacity={0.15}
              glareColor="#3b82f6"
              glarePosition="all"
              glareBorderRadius="20px"
            >
              <Card className="h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden group bg-white">
                {/* Vehicle Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={vehicle.image}
                    alt={`${vehicle.name} - AIS Express Fleet`}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${vehicle.color} opacity-20 group-hover:opacity-30 transition-opacity duration-500`} />
                  
                  {/* Vehicle Name Badge */}
                  <div className={`absolute top-4 left-4 bg-gradient-to-r ${vehicle.color} text-white px-4 py-2 rounded-full font-bold shadow-lg`}>
                    {vehicle.name}
                  </div>
                </div>

                <CardContent className="p-6">
                  {/* Description */}
                  <p className="text-slate-600 text-sm mb-4 italic">
                    {vehicle.description}
                  </p>

                  {/* Specifications */}
                  <div className="space-y-3">
                    <h3 className="font-bold text-slate-900 text-lg mb-3 flex items-center">
                      <Ruler className="h-5 w-5 mr-2 text-blue-600" />
                      Cargo Specifications
                    </h3>

                    {/* Dimensions */}
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-blue-50 p-2 rounded-lg text-center">
                        <div className="font-semibold text-blue-900">Length</div>
                        <div className="text-blue-600 font-bold">{vehicle.specs.length}</div>
                      </div>
                      <div className="bg-purple-50 p-2 rounded-lg text-center">
                        <div className="font-semibold text-purple-900">Height</div>
                        <div className="text-purple-600 font-bold">{vehicle.specs.height}</div>
                      </div>
                      <div className="bg-emerald-50 p-2 rounded-lg text-center">
                        <div className="font-semibold text-emerald-900">Width</div>
                        <div className="text-emerald-600 font-bold">{vehicle.specs.width}</div>
                      </div>
                    </div>

                    {/* Capacity */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                      <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-orange-600" />
                        <span className="text-slate-700 font-medium">
                          {vehicle.specs.pallets}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Weight className="h-5 w-5 text-red-600" />
                        <span className="text-slate-700 font-medium">
                          Max: {vehicle.specs.maxWeight}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Tilt>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-3">
            Need Help Choosing the Right Vehicle?
          </h3>
          <p className="text-slate-700 mb-6 max-w-2xl mx-auto">
            Our team can help you select the perfect vehicle for your delivery requirements. All vehicles come with 50k£ goods in transit insurance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:01344508095"
              className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              📞 Call 01344 508095
            </a>
            <a 
              href="/book"
              className="inline-flex items-center justify-center bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300"
            >
              Get a Quote
            </a>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, -60px) scale(1.1); }
          66% { transform: translate(-30px, 30px) scale(0.9); }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float 20s ease-in-out infinite;
          animation-delay: 10s;
        }
      `}</style>
    </section>
  );
};
